import { View, Text } from 'react-native'
import { mesaColorNames, typography } from '@/src/common/theme'
import { type ComponentProps, type PropsWithChildren, useState } from 'react'
import { isEqual, startCase } from 'lodash'
import { useMutation } from '@apollo/client'
import { Mutations } from '../gql'
import { Button } from '@/src/common/components/Button'
import { useLogError } from '@/src/common/hooks/useLogError'
import { GET_NOTIFICATION_SETTINGS } from '../gql/queries'
import { Category } from '../screens/Settings'
import { Switch } from '@/src/common/components/Switch'
import Toast from 'react-native-toast-message'
import type {
  NotificationSettings,
  UpdateNotificationSettingsMutationVariables,
} from '@api/mesa/graphql'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'

function CategorySettingsGated({
  categoryName,
  settings,
  children,
}: {
  categoryName: Category
  settings: NotificationSettings
  children: (props: {
    categorySettings?: PropsWithChildren['children']
    updateButton?: PropsWithChildren['children']
  }) => PropsWithChildren['children']
}) {
  const Alert = useAlert(CategorySettingsGated.name)
  const onError = useLogError()

  const { __typename, ...initialSelectedCategorySettings } = settings[categoryName]
  const [selectedCategorySettings, setSelectedCategorySettings] = useState(
    initialSelectedCategorySettings,
  )

  const [updateNotificationSettings, { loading: updateNotificationSettingsIsLoading }] =
    useMutation(Mutations.UPDATE_NOTIFICATION_SETTINGS, {
      refetchQueries: [GET_NOTIFICATION_SETTINGS],
    })

  const categorySettings = Object.entries(selectedCategorySettings).map(([name, settingItem]) => (
    <View key={`${categoryName}-${name}-settings`} style={{ marginLeft: 12, paddingBottom: 16 }}>
      <View style={{ flexDirection: 'row', paddingVertical: 4 }}>
        <View style={{ paddingTop: 5 }}>
          <Text style={{ fontSize: 16, color: mesaColorNames['Greyscale/900 #152438'] }}>
            {startCase(name.trim())}
          </Text>
        </View>
      </View>

      <View style={{ marginVertical: 8, marginLeft: 12, gap: 8 }}>
        <Switch
          checked={settingItem.email}
          onCheckedChange={(checked) =>
            setSelectedCategorySettings({
              ...selectedCategorySettings,
              [name]: { ...settingItem, email: checked },
            })
          }
          label={{
            viewProps: { style: { justifyContent: 'space-between' } },
            render: ({ switchComponent }) => (
              <>
                <Text
                  style={[
                    typography['paragraph/sm'],
                    { color: mesaColorNames['Greyscale/600 #505D64'] },
                  ]}
                >
                  Email
                </Text>
                {switchComponent}
              </>
            ),
          }}
        />
        <Switch
          checked={settingItem.push_notification}
          onCheckedChange={(checked) =>
            setSelectedCategorySettings({
              ...selectedCategorySettings,
              [name]: { ...settingItem, push_notification: checked },
            })
          }
          label={{
            viewProps: { style: { justifyContent: 'space-between' } },
            render: ({ switchComponent }) => (
              <>
                <Text
                  style={[
                    typography['paragraph/sm'],
                    { color: mesaColorNames['Greyscale/600 #505D64'] },
                  ]}
                >
                  Push Notification
                </Text>
                {switchComponent}
              </>
            ),
          }}
        />
      </View>
    </View>
  ))

  const updateButton = (
    <Button
      segmentAction='User Pressed "Update" Notification Settings'
      title="Update"
      disabled={
        updateNotificationSettingsIsLoading ||
        isEqual(initialSelectedCategorySettings, selectedCategorySettings)
      }
      loading={updateNotificationSettingsIsLoading}
      style={{ marginBottom: 32, marginTop: 8 }}
      onPress={() => {
        const variables = {
          settings: { ...settings, [categoryName]: selectedCategorySettings },
        } as const satisfies UpdateNotificationSettingsMutationVariables

        void updateNotificationSettings({
          variables,
          onCompleted(data) {
            switch (data.updateNotificationSettings.__typename) {
              case 'MesaGenericOk':
                Toast.show({
                  type: 'success',
                  text1: `${startCase(categoryName.trim())} Settings Updated`,
                  position: 'bottom',
                })
                break
              case 'MesaUserError':
                Alert.alert(
                  `We weren't able to update your notification settings`,
                  data.updateNotificationSettings.message || 'Please try again later',
                  [{ text: 'Back' }],
                )
                break
              default:
                onError('Failed to update user notification settings', variables)
                break
            }
          },
          onError,
        })
      }}
    />
  )

  return children({ categorySettings, updateButton })
}

export function CategorySettings(
  props: Omit<ComponentProps<typeof CategorySettingsGated>, 'categoryName'> &
    Partial<Pick<ComponentProps<typeof CategorySettingsGated>, 'categoryName'>>,
) {
  return props.categoryName ? (
    <CategorySettingsGated categoryName={props.categoryName} {...props} />
  ) : (
    props.children({})
  )
}
